import { useState } from 'react';
import VoiceFormFiller from '@/components/VoiceFormFiller';
import { appendText, textField, vText, type Extracted } from '@/lib/voiceFill';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';
import { useEncounterMutation, useWards } from '../hooks';
import { assignBed, decideDisposition, depart, startObservation } from '../api';
import { DISPOSITION_LABEL, Section, errorInfo, minutesLabel, timeLabel } from '../ui';
import type { Disposition, Workspace } from '../types';

// Who signed, who witnessed and who declared a death are legal attestations
// typed by the person responsible — never dictated on their behalf.
const NOT_DICTATED = new Set(['signed_by', 'witness', 'declared_by']);

type FieldDef = { key: string; label: string; kind: 'text' | 'area' | 'time' | 'check'; required?: boolean; hint?: string };

// The mandatory data set per disposition (D6) — mirrors utils/erStatus.js's
// validateDispositionData, which is what actually enforces it.
const FORMS: Record<Disposition, FieldDef[]> = {
  discharged: [
    { key: 'discharge_advice', label: 'Discharge advice', kind: 'area', required: true },
    { key: 'diagnosis', label: 'Diagnosis', kind: 'text' },
    { key: 'follow_up', label: 'Follow-up', kind: 'text', hint: 'e.g. Ortho OPD in 1 week' },
  ],
  admitted_ward: [{ key: 'reason', label: 'Reason for admission', kind: 'area', required: true }, { key: 'provisional_diagnosis', label: 'Provisional diagnosis', kind: 'text' }],
  admitted_icu: [{ key: 'reason', label: 'Reason for ICU admission', kind: 'area', required: true }, { key: 'provisional_diagnosis', label: 'Provisional diagnosis', kind: 'text' }],
  to_ot: [{ key: 'reason', label: 'Procedure / reason', kind: 'area', required: true }, { key: 'provisional_diagnosis', label: 'Provisional diagnosis', kind: 'text' }],
  referred_out: [
    { key: 'destination', label: 'Destination hospital', kind: 'text', required: true },
    { key: 'reason', label: 'Reason for referral', kind: 'text', required: true },
    { key: 'transfer_note', label: 'Transfer note (condition, treatment given)', kind: 'area', required: true },
    { key: 'escort', label: 'Escort', kind: 'text' },
    { key: 'ambulance', label: 'Ambulance / vehicle', kind: 'text' },
  ],
  lama_dama: [
    { key: 'risks_explained', label: 'Risks of leaving explained to the patient / attendant', kind: 'check', required: true },
    { key: 'refusal_form_signed', label: 'LAMA / DAMA form signed', kind: 'check', required: true },
    { key: 'signed_by', label: 'Signed by (name, relationship)', kind: 'text', required: true },
    { key: 'witness', label: 'Witness', kind: 'text', required: true },
    { key: 'notes', label: 'Notes', kind: 'area' },
  ],
  absconded: [
    { key: 'last_seen_at', label: 'Last seen at', kind: 'time', required: true },
    { key: 'police_intimation', label: 'Police informed (how / when) — required for an MLC', kind: 'text' },
    { key: 'notes', label: 'Notes', kind: 'area' },
  ],
  lwbs: [{ key: 'last_seen_at', label: 'Last seen at (optional)', kind: 'time' }, { key: 'notes', label: 'Notes', kind: 'area' }],
  death: [
    { key: 'time_of_death', label: 'Time of death', kind: 'time', required: true },
    { key: 'cause_of_death', label: 'Cause of death (or "under investigation")', kind: 'area', required: true },
    { key: 'mccd_issued', label: 'MCCD issued', kind: 'check' },
    { key: 'mccd_note', label: 'If no MCCD, why (e.g. MLC — sent for post-mortem)', kind: 'text' },
    { key: 'police_informed', label: 'Police informed (MLC)', kind: 'text' },
  ],
  brought_in_dead: [
    { key: 'time_of_death', label: 'Time death was declared', kind: 'time', required: true },
    { key: 'declared_by', label: 'Declared by (doctor)', kind: 'text', required: true },
    { key: 'police_informed', label: 'Police informed', kind: 'text' },
    { key: 'notes', label: 'Findings / history from bystanders', kind: 'area' },
  ],
};

const ADMIT: Disposition[] = ['admitted_ward', 'admitted_icu', 'to_ot'];

/**
 * Shown when the chosen move needs a verified identity the patient doesn't
 * have. Resolve it first; only a Doctor/Admin may go ahead without, by
 * recording why it can't be resolved (unconscious, no attender...). The server
 * enforces the same rule (utils/erStatus.checkIdentityForDisposition).
 */
function IdentityGate({ message, canOverride, reason, onReason, onResolve }: {
  message: string; canOverride: boolean; reason: string; onReason: (v: string) => void; onResolve?: () => void;
}) {
  return (
    <div className="space-y-2 rounded-lg bg-danger-soft p-2.5 text-[12px] text-danger-ink">
      <div className="font-semibold">🪪 {message}</div>
      {onResolve && <Button size="sm" variant="ghost" onClick={onResolve}>❓ Resolve identity</Button>}
      {canOverride ? (
        <FormField label="Identity can't be resolved — why? (Doctor/Admin)">
          <Textarea rows={2} value={reason} placeholder="e.g. Unconscious, no attender, no documents — needs ICU now"
            onChange={(e) => onReason(e.target.value)} />
        </FormField>
      ) : (
        <div>Only a Doctor or Admin can proceed without a verified identity.</div>
      )}
    </div>
  );
}

function BedPicker({ value, onChange }: { value: number | ''; onChange: (v: number | '') => void }) {
  const wards = useWards();
  const options = (wards.data ?? []).filter((w) => w.active).flatMap((w) => w.beds.filter((b) => b.status === 'available').map((b) => ({ id: b.id, label: `${w.name} · bed ${b.bed_number}` })));
  return (
    <NativeSelect value={value} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}>
      <option value="">{wards.isLoading ? 'Loading beds…' : options.length ? 'No bed yet (boarding)' : 'No bed available (boarding)'}</option>
      {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </NativeSelect>
  );
}

/**
 * Disposition (§5.3, §7). The DECISION and the DEPARTURE are separate actions
 * because the gap between them is boarding (D9). Open items are shown as
 * warnings, never gates. Death and brought-in-dead record the deceased status
 * in the same transaction; an admission rolls the ED charges into the IP bill
 * in the same transaction.
 */
export default function DispositionSection({ ws, onResolveIdentity }: { ws: Workspace; onResolveIdentity?: () => void }) {
  const { user } = useAuth();
  const enc = ws.encounter;
  const id = enc.id;
  const canAct = !!user?.modules?.includes('er:disposition');
  const [choice, setChoice] = useState<Disposition | ''>('');
  const [data, setData] = useState<Record<string, string | boolean>>({});
  const [bed, setBed] = useState<number | ''>('');
  const [correcting, setCorrecting] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [departBed, setDepartBed] = useState<number | ''>('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [identityReason, setIdentityReason] = useState('');
  const [confirmDeath, setConfirmDeath] = useState(false);

  const decide = useEncounterMutation(id, (body: Parameters<typeof decideDisposition>[1]) => decideDisposition(id, body));
  const obs = useEncounterMutation(id, () => startObservation(id));
  const admit = useEncounterMutation(id, (bedId: number) => assignBed(id, bedId, identityReason.trim() || undefined));
  const leave = useEncounterMutation(id, (body: Parameters<typeof depart>[1]) => depart(id, body));

  const allowed = ws.can.dispositions;
  const idGate = (code: Disposition | '' | null | undefined) => (code ? allowed.find((d) => d.code === code) : undefined);
  const chosenGate = idGate(choice);
  const choiceBlocked = !!chosenGate?.identity_required && (!ws.can.identity_override || !identityReason.trim());
  const submit = async (deathConfirmed = false) => {
    if (!choice) return;
    setErrors({});
    const payload: Record<string, unknown> = {};
    for (const f of FORMS[choice]) {
      const v = data[f.key];
      if (v === undefined || v === '') continue;
      payload[f.key] = f.kind === 'time' ? new Date(v as string).toISOString() : v;
    }
    // A death is confirmed in a dialog first — window.confirm() is refused by embedded browsers.
    if (DEATH(choice) && !deathConfirmed) { setConfirmDeath(true); return; }
    try {
      await decide.mutateAsync({
        disposition: choice, data: payload, bed_id: ADMIT.includes(choice) && bed ? bed : undefined,
        correction_reason: correcting ? correctionReason : undefined,
        identity_override_reason: chosenGate?.identity_required ? identityReason.trim() : undefined,
      });
      toast({ title: `Disposition: ${DISPOSITION_LABEL[choice]}`, variant: 'success' });
      setChoice(''); setData({}); setCorrecting(false); setCorrectionReason(''); setIdentityReason('');
    } catch (e) {
      const info = errorInfo(e);
      setErrors(info.fields);
      toast({ title: 'Disposition not recorded', description: info.message, variant: 'danger' });
    }
  };

  const doDepart = async () => {
    try {
      const r = await leave.mutateAsync({ bed_id: departBed || undefined, payment_mode: paymentMode, identity_override_reason: identityReason.trim() || undefined });
      toast({ title: 'Patient departed the ED', description: r.bill ? `ER bill ${r.bill.bill_number} raised (₹${Number(r.bill.total_amount).toFixed(2)}, pending collection).` : undefined, variant: 'success' });
    } catch (e) { toast({ title: 'Could not record departure', description: errorInfo(e).message, variant: 'danger' }); }
  };

  // ── Departed ──
  if (enc.status === 'departed') {
    return (
      <Section title="Disposition">
        <div className="space-y-1 text-[13px]">
          <div><Badge variant="neutral">{enc.disposition ? DISPOSITION_LABEL[enc.disposition] : '—'}</Badge> decided {timeLabel(enc.decision_at)} by {enc.disposition_by}; departed {timeLabel(enc.departed_at)}.</div>
          {enc.decision_at && enc.departed_at && <div className="text-ink-muted">Boarding: {minutesLabel((new Date(enc.departed_at).getTime() - new Date(enc.decision_at).getTime()) / 60000)}</div>}
          {ws.admission && <div>Admitted to {ws.admission.ward_name}, bed {ws.admission.bed_number} (admission #{ws.admission.id}) — ED charges are on the IP bill.</div>}
          {ws.bill && <div>ER bill {ws.bill.bill_number} · ₹{Number(ws.bill.total_amount).toFixed(2)} · {ws.bill.payment_status}</div>}
          <DataView data={enc.disposition_data} />
        </div>
      </Section>
    );
  }

  const decided = enc.status === 'disposition_decided' && !correcting;
  const needsBed = decided && enc.disposition && ADMIT.includes(enc.disposition) && !enc.admission_id;
  // A decision recorded with an override carries it; otherwise the move needs a
  // verified identity (or an override given now) — e.g. decided before identity was gated.
  const decidedGate = decided && !enc.disposition_data?.identity_override ? idGate(enc.disposition) : undefined;
  const departBlocked = !!decidedGate?.identity_required && (!ws.can.identity_override || !identityReason.trim());

  return (
    <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
      {decided ? (
        <Section title="Disposition decided">
          <div className="space-y-2 text-[13px]">
            <div><Badge variant="info">{enc.disposition ? DISPOSITION_LABEL[enc.disposition] : ''}</Badge> at {timeLabel(enc.decision_at)} by {enc.disposition_by}</div>
            {ws.clocks.boarding && <div className={cn(ws.clocks.boarding.alert && 'font-semibold text-danger-ink')}>Boarding for {minutesLabel(ws.clocks.boarding.minutes)}</div>}
            <DataView data={enc.disposition_data} />
            {ws.admission && <div>Admission #{ws.admission.id} — {ws.admission.ward_name}, bed {ws.admission.bed_number}.</div>}
            {canAct && decidedGate?.identity_required && (
              <IdentityGate message={decidedGate.identity_reason ?? ''} canOverride={ws.can.identity_override}
                reason={identityReason} onReason={setIdentityReason} onResolve={onResolveIdentity} />
            )}
            {canAct && needsBed && (
              <div className="rounded-lg bg-warn-soft p-2">
                <div className="mb-1 text-[12px] font-semibold text-warn-ink">No bed yet — the patient is boarding. Assign a bed to create the admission.</div>
                <div className="flex gap-2">
                  <div className="flex-1"><BedPicker value={departBed} onChange={setDepartBed} /></div>
                  <Button size="sm" disabled={!departBed || departBlocked} loading={admit.isPending}
                    onClick={() => admit.mutateAsync(departBed as number).then(() => toast({ title: 'Admission created', variant: 'success' })).catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }))}>Assign bed</Button>
                </div>
              </div>
            )}
            {canAct && (
              <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
                {!(enc.disposition && ADMIT.includes(enc.disposition)) && (
                  <FormField label="Payment mode for the ER bill">
                    <NativeSelect value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                      <option>Cash</option><option>Card</option><option>UPI</option><option>Insurance</option><option>Credit</option>
                    </NativeSelect>
                  </FormField>
                )}
                <Button variant="success" loading={leave.isPending} disabled={(!!needsBed && !departBed) || departBlocked} onClick={doDepart}>🚪 Patient has left the ED</Button>
                {ws.can.correct_disposition && <Button variant="ghost" onClick={() => { setCorrecting(true); setChoice(''); }}>Correct disposition…</Button>}
              </div>
            )}
          </div>
        </Section>
      ) : (
        <Section title={correcting ? 'Correct the disposition' : 'Decide disposition'}>
          {!canAct ? <div className="text-[13px] text-ink-muted">You don’t have permission to decide a disposition.</div> : (
            <>
              {enc.status !== 'observation' && ws.can.observation && (
                <div className="mb-3 flex items-center justify-between rounded-lg bg-muted px-3 py-2 text-[12px]">
                  <span>Not ready to decide? Start observation (max {ws.settings.obs_max_hours} h).</span>
                  <Button size="sm" variant="ghost" loading={obs.isPending} onClick={() => obs.mutateAsync(undefined).catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }))}>⌛ Observation</Button>
                </div>
              )}
              {enc.status === 'observation' && ws.clocks.observation && (
                <div className={cn('mb-3 rounded-lg px-3 py-2 text-[12px]', ws.clocks.observation.breached ? 'bg-danger-soft text-danger-ink' : 'bg-muted')}>
                  Under observation since {timeLabel(ws.clocks.observation.started_at)} — {ws.clocks.observation.breached ? 'limit exceeded, decide now' : `${minutesLabel(ws.clocks.observation.minutes_left)} left`}.
                </div>
              )}
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {allowed.map((d) => (
                  <button key={d.code} type="button" disabled={!d.allowed && !correcting} title={d.reason ?? undefined}
                    onClick={() => { setChoice(d.code); setData({}); setErrors({}); setIdentityReason(''); }}
                    className={cn('rounded-lg border px-2.5 py-2 text-left text-[12px] font-medium transition',
                      choice === d.code ? 'border-teal bg-accent text-teal' : 'border-border hover:bg-muted',
                      !d.allowed && !correcting && 'cursor-not-allowed opacity-40')}>
                    {d.label}
                    {d.identity_required && <span className="block text-[10px] font-normal text-danger-ink">🪪 Identity needed</span>}
                  </button>
                ))}
              </div>
              {choice && (
                <div className="mt-3 space-y-2">
                  {(() => {
                    const fields = FORMS[choice].filter((f) => (f.kind === 'text' || f.kind === 'area') && !NOT_DICTATED.has(f.key));
                    if (!fields.length) return null;
                    const apply = (x: Extracted) => setData((cur) => {
                      const next = { ...cur };
                      for (const f of fields) {
                        const t = vText(x, f.key);
                        if (t) next[f.key] = f.kind === 'area' ? appendText(cur[f.key] as string | undefined, t, '\n') : t;
                      }
                      return next;
                    });
                    return (
                      <VoiceFormFiller key={choice} fields={fields.map((f) => textField(f.key, f.label))} onFields={apply}
                        label="🎙 Dictate details"
                        hint={choice === 'discharged' ? 'Diagnosis: sprain right ankle; advice: rest, ice, elevation, painkillers for 5 days; follow-up ortho OPD in 1 week' : 'Say each detail by name'} />
                    );
                  })()}
                  {FORMS[choice].map((f) => f.kind === 'check' ? (
                    <label key={f.key} className="flex items-center gap-2 text-[12px]">
                      <Checkbox checked={data[f.key] === true} onCheckedChange={(v) => setData((x) => ({ ...x, [f.key]: v === true }))} />
                      {f.label}{f.required && <span className="text-danger"> *</span>}
                      {errors[f.key] && <span className="text-danger">— {errors[f.key]}</span>}
                    </label>
                  ) : (
                    <FormField key={f.key} label={f.label} required={f.required} error={errors[f.key]}>
                      {f.kind === 'area' ? <Textarea rows={2} value={(data[f.key] as string) ?? ''} onChange={(e) => setData((x) => ({ ...x, [f.key]: e.target.value }))} />
                        : <Input type={f.kind === 'time' ? 'datetime-local' : 'text'} placeholder={f.hint} value={(data[f.key] as string) ?? ''} onChange={(e) => setData((x) => ({ ...x, [f.key]: e.target.value }))} />}
                    </FormField>
                  ))}
                  {ADMIT.includes(choice) && (
                    <FormField label="Bed (optional now — without one the patient boards in the ED)">
                      <BedPicker value={bed} onChange={setBed} />
                    </FormField>
                  )}
                  {chosenGate?.identity_required && (
                    <IdentityGate message={chosenGate.identity_reason ?? ''} canOverride={ws.can.identity_override}
                      reason={identityReason} onReason={setIdentityReason} onResolve={onResolveIdentity} />
                  )}
                  {correcting && (
                    <FormField label="Reason for correcting the disposition" required>
                      <Input value={correctionReason} onChange={(e) => setCorrectionReason(e.target.value)} />
                    </FormField>
                  )}
                  <div className="flex justify-end gap-2">
                    {correcting && <Button variant="ghost" onClick={() => setCorrecting(false)}>Cancel</Button>}
                    <Button variant={DEATH(choice) ? 'danger' : 'primary'} loading={decide.isPending} disabled={(correcting && !correctionReason.trim()) || choiceBlocked} onClick={() => submit()}>
                      {correcting ? 'Save correction' : `Confirm: ${DISPOSITION_LABEL[choice]}`}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Section>
      )}

      <Section title="Before the patient leaves">
        {ws.open_items.length === 0 ? <div className="text-[13px] text-success-ink">No open items.</div> : (
          <>
            <ul className="space-y-1 text-[12px]">
              {ws.open_items.map((i, k) => (
                <li key={k} className={i.severity === 'red' ? 'font-semibold text-danger-ink' : i.severity === 'amber' ? 'text-warn-ink' : 'text-ink-muted'}>• {i.message}</li>
              ))}
            </ul>
            <div className="mt-2 text-[11px] text-ink-muted">These are warnings — except identity, none of them blocks a disposition. Hand pending items over in a handover note.</div>
          </>
        )}
      </Section>

      <ConfirmDialog
        open={confirmDeath}
        onOpenChange={setConfirmDeath}
        title="Record the patient as deceased?"
        description="This records the patient as DECEASED on their record at every branch, cancels their booked appointments and stops patient messages. It can only be undone by a recorded correction."
        confirmLabel={choice ? `Confirm: ${DISPOSITION_LABEL[choice]}` : 'Confirm'}
        onConfirm={() => { void submit(true); }}
      />
    </div>
  );
}

function DEATH(d: Disposition | '') { return d === 'death' || d === 'brought_in_dead'; }

function DataView({ data }: { data: Record<string, unknown> | null }) {
  if (!data) return null;
  const override = data.identity_override as { reason?: string; by?: string; at?: string; identity_status?: string } | undefined;
  const entries = Object.entries(data).filter(([k, v]) => k !== 'identity_override' && v !== '' && v !== null && v !== undefined);
  if (!entries.length && !override) return null;
  return (
    <>
    {override && (
      <div className="rounded-md bg-warn-soft px-2 py-1 text-[12px] text-warn-ink">
        🪪 Moved without a verified identity ({override.identity_status}) — {override.reason} · {override.by}, {timeLabel(override.at ?? null)}
      </div>
    )}
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[12px]">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="capitalize text-ink-muted">{k.replace(/_/g, ' ')}</dt>
          <dd>{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : /\d{4}-\d{2}-\d{2}T/.test(String(v)) ? timeLabel(String(v)) : String(v)}</dd>
        </div>
      ))}
    </dl>
    </>
  );
}
